import {
  addPollingIds,
  getPollingIds,
  removePollingId,
  addTerminalAssessmentId,
  isTerminalAssessment,
} from "@root/utils/pollingQueue";
import { RiskAssessmentService } from "@root/services/risk-assessment";
import {
  RISK_ASSESSMENT_POLLING_INTERVAL,
  RISK_ASSESSMENT_STATUS,
  ASSESSMENT_REVIEW_STATUSES,
  TOAST_VARIANT,
} from "@root/constants/common";
import { useDispatch } from "react-redux";
import { showAlert } from "@root/store/slices/features";
import { ROUTE_NAMES } from "@root/constants";
import { useTranslation } from "react-i18next";

// Module-level singletons so polling continues across route unmounts
const pollingRefs: Record<string, NodeJS.Timeout | null> = {};
const riskAssessmentService = new RiskAssessmentService();

type OnItemUpdate = (item: any) => void;

interface UseAssessmentPollingQueueOptions {
  onItemUpdate: OnItemUpdate;
  intervalMs?: number;
}

/**
 * Manages a polling queue for assessment items across route changes.
 * Persists IDs in localStorage and restarts polling when the listing page mounts again.
 */
export function useAssessmentPollingQueue(
  options: UseAssessmentPollingQueueOptions
) {
  const { onItemUpdate, intervalMs = RISK_ASSESSMENT_POLLING_INTERVAL } =
    options;
  const dispatch = useDispatch();
  const { t } = useTranslation("common");

  const ensurePolling = (id: string) => {
    if (!id) return;
    if (pollingRefs[id]) return; // already polling

    if (isTerminalAssessment(id)) {
      removePollingId(id); // Clean up from localStorage if it's there
      return;
    }

    // Persist ID to storage
    addPollingIds(id);

    // Start interval
    const tick = async () => {
      try {
        const response = await riskAssessmentService.getRiskAssessmentById(id);
        console.log("response here", response);
        // Support both apiResponse.data and apiResponse.data.data shapes
        const item = response?.data?.data ?? response?.data;
        console.log("item here", item);
        if (item) {
          onItemUpdate(item);
          const status = item?.status;

          const isCompleted =
            status === RISK_ASSESSMENT_STATUS.COMPLETED ||
            status === ASSESSMENT_REVIEW_STATUSES.COMPLETED;
          const isException =
            status === RISK_ASSESSMENT_STATUS.EXCEPTION ||
            status === ASSESSMENT_REVIEW_STATUSES.EXCEPTION;
          const isIrrelevant =
            status === ASSESSMENT_REVIEW_STATUSES.IRRELEVANT_DOC;

          // Stop polling when assessment is either COMPLETED, EXCEPTION, or IRRELEVANT_DOC state
          if (isCompleted || isException || isIrrelevant) {
            stopPolling(id);
            removePollingId(id);

            if (!isTerminalAssessment(id)) {
              addTerminalAssessmentId(id);

              const messageKey = isException
                ? "assessments.assessmentExceptionToast"
                : isIrrelevant
                ? "assessments.assessmentIrrelevantToast"
                : "assessments.assessmentCompletedToast";

              const message = t(messageKey, {
                name: item?.name,
              });

              dispatch(
                showAlert({
                  type: isException
                    ? TOAST_VARIANT.ERROR
                    : isIrrelevant
                    ? TOAST_VARIANT.WARNING
                    : TOAST_VARIANT.SUCCESS,
                  message,
                  redirectTo: `${ROUTE_NAMES.RISK_ASSESSMENT}?assessmentType=${item?.assessmentType}&assessmentId=${item?.id}`,
                })
              );
            }
            return; // Exit early to prevent any further processing
          }
        }
      } catch {
        // If error, keep polling (transient). Could add backoff if needed
      }
    };

    // Run immediately, then on interval
    tick();
    pollingRefs[id] = setInterval(tick, intervalMs);
  };

  const stopPolling = (id: string) => {
    const ref = pollingRefs[id];
    if (ref) {
      clearInterval(ref);
      pollingRefs[id] = null;
      delete pollingRefs[id];
    }
  };

  const start = (ids: string[]) => {
    const existing = getPollingIds();
    const all = Array.from(new Set([...(ids || []), ...existing]));
    all.forEach((id) => ensurePolling(String(id)));
  };

  return { start, stopPolling };
}
